/**
 * Centralized Storage Key Management
 *
 * CRITICAL: Only email should persist to localStorage.
 * Delivery info should be entered fresh each time (privacy requirement).
 * Everything else should use sessionStorage (clears on tab close).
 */

// ============================================================================
// LOCALSTORAGE KEYS (Persist forever - use sparingly!)
// ============================================================================

export const PERSISTENT_KEYS = {
  /** User's email address - OK to remember between sessions */
  EMAIL: 'innolek_user_email',

  /** Language preference */
  LANGUAGE: 'i18nextLng',

  /** Locale/currency preference */
  LOCALE: 'innolek_locale',

  /** ❌ REMOVED: DELIVERY_INFO - Privacy violation, moved to cleanup */
  /** @deprecated Use fresh form input instead */
  DELIVERY_INFO: 'innolek_delivery_info', // Keep for cleanup utility reference
} as const;

// ============================================================================
// SESSIONSTORAGE KEYS (Clear on tab close - use for wizard state)
// ============================================================================

export const SESSION_KEYS = {
  /** Wizard store state - should reset between sessions */
  WIZARD_STORE: 'wizard-store-session',

  /** Mascot wizard store state */
  MASCOT_STORE: 'mascot-store-session',

  /** AI generation store state */
  AI_STORE: 'wizard-ai-store-session',

  /** Current session ID */
  SESSION_ID: 'innolek_session_id',

  /** Current order ID for this session */
  SESSION_ORDER_ID: 'innolek_session_order_id',

  /** Business logic cache flag */
  BUSINESS_LOGIC_CACHED: 'business-logic-cached',

  /** Auth redirect path */
  AUTH_REDIRECT: 'auth-redirect',

  /** Last payment intent */
  LAST_PAYMENT_INTENT: 'last_payment_intent',

  /** Scroll targets from summary panel */
  SCROLL_TO_CATEGORY: 'scrollToCategoryId',
  SCROLL_TO_COLOR: 'scrollToColorId',
  SCROLL_TO_STYLE: 'scrollToStyleId',
  SCROLL_TO_PERSONALITY: 'scrollToPersonalityId',

  /** Regeneration trigger flag */
  TRIGGER_REGENERATION: 'triggerRegeneration',

  /** Wizard just reset flag */
  WIZARD_JUST_RESET: 'wizard_just_reset',

  /** Preview validation log */
  PREVIEW_VALIDATION_LOG: 'lastPreviewValidationLog',

  /** Delivery info - persists across page refresh but clears on tab close */
  DELIVERY_INFO: 'innolek_delivery_info',
} as const;

// ============================================================================
// DEPRECATED KEYS (Should be migrated/removed)
// ============================================================================

export const DEPRECATED_KEYS = {
  /** Old email keys - consolidate to PERSISTENT_KEYS.EMAIL */
  OLD_EMAIL_KEYS: ['userEmail', 'wizard-email', 'saved_email'],

  /** Old wizard store key - migrated to SESSION_KEYS.WIZARD_STORE */
  OLD_WIZARD_STORE: 'wizard-store',

  /** Old mascot store key - migrated to SESSION_KEYS.MASCOT_STORE */
  OLD_MASCOT_STORE: 'mascot-store',

  /** Old AI store key - migrated to SESSION_KEYS.AI_STORE */
  OLD_AI_STORE: 'wizard-ai-store',

  /** Email prompt flag - should not persist */
  EMAIL_PROMPT_FLAG: 'hasShownEmailPrompt',
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get user email from all available sources in priority order
 *
 * Priority:
 * 1. wizardData.email (from wizard store - most current)
 * 2. localStorage.getItem('innolek_user_email') (persisted email)
 * 3. Firebase user email (from authService - authenticated user)
 * 4. null (if none available)
 *
 * This ensures order lookups use the same email that was used to create the order.
 *
 * ⚠️ Note: This function uses dynamic imports to avoid circular dependencies.
 * For synchronous contexts, use the synchronous version below.
 *
 * @returns User email string or null if not available
 */
export async function getUserEmailForOrderLookup(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Try localStorage (primary source in v2)
    const localStorageEmail = window.localStorage.getItem(PERSISTENT_KEYS.EMAIL);
    if (
      localStorageEmail &&
      typeof localStorageEmail === 'string' &&
      localStorageEmail.trim().length > 0 &&
      localStorageEmail.includes('@')
    ) {
      return localStorageEmail.trim();
    }

    // 3. Try Firebase user email
    const { authService } = await import('@/features/auth');
    const currentUser = authService.getCurrentUser();
    const firebaseEmail = currentUser?.email;

    if (
      firebaseEmail &&
      typeof firebaseEmail === 'string' &&
      firebaseEmail.trim().length > 0 &&
      firebaseEmail.includes('@')
    ) {
      return firebaseEmail.trim();
    }

    // 4. No email available
    return null;
  } catch (_error) {
    // Silently fail - return null if any error occurs
    return null;
  }
}

/**
 * Get user email synchronously (for use in synchronous contexts)
 *
 * Priority:
 * 1. localStorage.getItem('innolek_user_email') (persisted email)
 * 2. null (if not available)
 *
 * ⚠️ Note: This version does not check wizard store or Firebase user to avoid circular dependencies.
 * Use async getUserEmailForOrderLookup() for complete email resolution.
 *
 * @returns User email string or null if not available
 */
export function getUserEmailSync(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const localStorageEmail = window.localStorage.getItem(PERSISTENT_KEYS.EMAIL);
    if (
      localStorageEmail &&
      typeof localStorageEmail === 'string' &&
      localStorageEmail.trim().length > 0 &&
      localStorageEmail.includes('@')
    ) {
      return localStorageEmail.trim();
    }
    return null;
  } catch (_error) {
    return null;
  }
}

/**
 * Get user email - Alias for getUserEmailSync for legacy support/tests
 */
export function getUserEmail(): string | null {
  return getUserEmailSync();
}

/**
 * Save user email to persistent storage
 */
export function saveUserEmail(email: string): void {
  if (typeof window === 'undefined') return;

  try {
    const trimmedEmail = email.trim();

    // Remove if empty
    if (!trimmedEmail) {
      window.localStorage.removeItem(PERSISTENT_KEYS.EMAIL);
      return;
    }

    window.localStorage.setItem(PERSISTENT_KEYS.EMAIL, trimmedEmail);
  } catch (_error) {
    // Silently ignore storage errors
  }
}

/**
 * Clean up deprecated keys from storage
 */
export function cleanupDeprecatedKeys(): void {
  if (typeof window === 'undefined') return;

  try {
    // Remove old email keys
    DEPRECATED_KEYS.OLD_EMAIL_KEYS.forEach((key) => {
      window.localStorage.removeItem(key);
    });

    // Remove email prompt flag
    window.localStorage.removeItem(DEPRECATED_KEYS.EMAIL_PROMPT_FLAG);

    // Remove old delivery info (privacy)
    const legacyDeliveryInfo = 'innolek_delivery_info';
    window.localStorage.removeItem(legacyDeliveryInfo);
  } catch (_error) {
    /* Ignore */
  }
}

/**
 * Migrate legacy data to session storage
 */
export function migrateToSessionStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    // Move wizard store if it exists in local (it shouldn't, but cleanup)
    const oldWizardStore = window.localStorage.getItem(DEPRECATED_KEYS.OLD_WIZARD_STORE);
    if (oldWizardStore) {
      window.localStorage.removeItem(DEPRECATED_KEYS.OLD_WIZARD_STORE);
      // We don't actually migrate to session to strict privacy, just clear from local
    }

    // Clear other old stores
    window.localStorage.removeItem(DEPRECATED_KEYS.OLD_MASCOT_STORE);
    window.localStorage.removeItem(DEPRECATED_KEYS.OLD_AI_STORE);
  } catch (_error) {
    /* Ignore */
  }
}
